/**
 * Recording a partner putting money in, or taking it out (BR-03, BR-04).
 *
 * This was missing, and its absence was quiet in the worst way: the partners
 * screen showed capital, ownership percentage and net position, the dashboard
 * showed "capital in", and the break-even bar counted toward ৳6,00,000 — all
 * of them permanently zero, because nothing in the app could record a
 * contribution. Every one of those figures is derived from `capital_in`
 * transactions (BR-01), so with no way to enter one they were not wrong so
 * much as answering a question nobody had been allowed to ask.
 *
 * Capital and drawings share this form because they are the same shape and the
 * same rule: BR-03 says each names the partner it belongs to, and both are
 * equity rather than profit and loss. Filing a drawing as an expense would
 * overstate the loss and tell four partners the farm is failing when it is not
 * — which is the whole reason those types exist separately.
 *
 * Both land pending, like everything else (BR-07), and BR-04 goes further:
 * whoever approves it must be neither the creator nor the partner the money
 * belongs to. The server enforces that; this screen only says so up front.
 */

import { useState, type FormEvent } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorMessageKey } from '../../lib/apiError.js';
import {
  AMOUNT_KEYS,
  appendAmountKey,
  formatAmountDraft,
  isCompleteAmount,
  toApiAmount,
} from '../../lib/amountInput.js';
import { todayInDhaka } from '../../lib/date.js';
import { useAccounts, useCreateTransaction, usePartners } from './api.js';

type Kind = 'capital_in' | 'drawing';

export function PartnerMoneyForm(): JSX.Element {
  const { t } = useTranslation();

  const { data: partners = [] } = usePartners();
  const { data: accounts = [] } = useAccounts();
  const createTransaction = useCreateTransaction();

  const [kind, setKind] = useState<Kind>('capital_in');
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [accountId, setAccountId] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [txnDate, setTxnDate] = useState(todayInDhaka());
  const [note, setNote] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // BR-03: without a partner this cannot be set against anyone at
  // distribution time, which is the entire purpose of recording it.
  const canSubmit =
    isCompleteAmount(amount) && partnerId !== null && accountId !== null && !createTransaction.isPending;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrorKey(null);
    setSaved(false);

    try {
      await createTransaction.mutateAsync({
        txnDate,
        type: kind,
        // A string all the way to the server; never a float (MONEY-1).
        amount: toApiAmount(amount),
        accountId: accountId!,
        partnerId: partnerId!,
        ...(note.trim() !== '' ? { note: note.trim() } : {}),
      });

      setAmount('');
      setNote('');
      setSaved(true);
    } catch (error) {
      setErrorKey(errorMessageKey(error));
    }
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="page-container" noValidate>
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('partnerMoney.title')}</h1>

      {/* Which direction the money went. Getting this wrong is the mistake
          that matters, so it is the first choice and not a dropdown. */}
      <div className="mb-4 grid grid-cols-2 gap-2" role="group" aria-label={t('partnerMoney.kind')}>
        {(['capital_in', 'drawing'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setKind(option)}
            aria-pressed={kind === option}
            className={`min-h-touch rounded-xl px-3 py-3 text-sm font-medium ${
              kind === option ? 'bg-green-700 text-white' : 'bg-white text-gray-700 shadow-sm'
            }`}
          >
            {t(option === 'capital_in' ? 'partnerMoney.putIn' : 'partnerMoney.tookOut')}
          </button>
        ))}
      </div>

      <div
        aria-label={t('finance.expense.amount')}
        aria-live="polite"
        className="mb-1 block w-full rounded-xl bg-white px-4 py-5 text-right text-4xl font-semibold tabular-nums text-gray-900 shadow-sm"
      >
        ৳ {formatAmountDraft(amount)}
      </div>

      <div className="mb-5 grid grid-cols-3 gap-2" role="group" aria-label={t('finance.expense.keypad')}>
        {AMOUNT_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setAmount((current) => appendAmountKey(current, key))}
            className="min-h-[56px] rounded-xl bg-white text-2xl font-medium text-gray-800 shadow-sm active:bg-gray-100"
          >
            {key}
          </button>
        ))}
      </div>

      <fieldset className="mb-4">
        <legend className="mb-2 text-sm font-medium text-gray-700">{t('partnerMoney.whose')}</legend>
        <div className="grid grid-cols-2 gap-2">
          {partners.map((partner) => (
            <button
              key={partner.partnerId}
              type="button"
              onClick={() => setPartnerId(partner.partnerId)}
              aria-pressed={partnerId === partner.partnerId}
              className={`min-h-touch truncate rounded-xl px-3 py-2 text-sm ${
                partnerId === partner.partnerId
                  ? 'bg-green-700 font-medium text-white'
                  : 'bg-white text-gray-700 shadow-sm'
              }`}
            >
              {partner.fullName}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="mb-4">
        <legend className="mb-2 text-sm font-medium text-gray-700">
          {t(kind === 'capital_in' ? 'partnerMoney.intoAccount' : 'partnerMoney.fromAccount')}
        </legend>
        <div className="flex flex-wrap gap-2">
          {accounts.map((account) => (
            <button
              key={account.id}
              type="button"
              onClick={() => setAccountId(account.id)}
              aria-pressed={accountId === account.id}
              className={`min-h-touch rounded-xl px-4 py-2 text-sm ${
                accountId === account.id ? 'bg-green-700 font-medium text-white' : 'bg-white text-gray-700 shadow-sm'
              }`}
            >
              {account.name}
            </button>
          ))}
        </div>
      </fieldset>

      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="pm-date">
        {t('partnerMoney.date')}
      </label>
      <input
        id="pm-date"
        type="date"
        value={txnDate}
        onChange={(event) => setTxnDate(event.target.value)}
        className="mb-4 min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
      />

      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="pm-note">
        {t('partnerMoney.note')}
      </label>
      <input
        id="pm-note"
        value={note}
        onChange={(event) => setNote(event.target.value)}
        maxLength={1000}
        className="mb-4 min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
      />

      {/* BR-04, said before the tap rather than discovered after it. */}
      <p className="mb-4 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-900">{t('partnerMoney.approvalNote')}</p>

      {errorKey !== null && (
        <p role="alert" className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          {t(errorKey)}
        </p>
      )}

      {saved && (
        <p role="status" className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">
          {t('partnerMoney.saved')}
        </p>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className="min-h-[52px] w-full rounded-xl bg-green-700 text-lg font-medium text-white disabled:opacity-50"
      >
        {t(createTransaction.isPending ? 'partnerMoney.saving' : 'partnerMoney.submit')}
      </button>
    </form>
  );
}
